import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Lock, Mail } from 'lucide-react';
import { routeByRole } from '@/utils/routeByRole';

const API = '/api';

/**
 * PIN Quick-Login form — Epic E.
 *
 * Returning-user shortcut: email + 4-digit PIN → session via
 * POST /api/auth/pin-login. Routes via routeByRole() on success.
 */
export default function PinLoginForm({ accentClass = 'bg-orange-600 hover:bg-orange-700' }) {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!email.includes('@')) {
      setError('Please enter a valid email.');
      return;
    }
    if (!/^\d{4}$/.test(pin)) {
      setError('PIN must be 4 digits.');
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/pin-login`, {
        email: email.trim().toLowerCase(),
        pin,
      });
      if (data.session_token) {
        localStorage.setItem('yukti_session_token', data.session_token);
      }
      const target = routeByRole(data);
      navigate(target, { state: { user: data }, replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Sign-in failed. Please try again.';
      setError(detail);
      setPin('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="pin-login-form">
      <label className="block">
        <span className="text-sm font-medium text-gray-700 mb-1.5 flex items-center gap-2">
          <Mail className="w-4 h-4" /> Email
        </span>
        <input
          type="email"
          required
          autoComplete="email"
          inputMode="email"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => { setEmail(e.target.value); setError(null); }}
          data-testid="pin-login-email-input"
          className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none transition"
        />
      </label>
      <label className="block">
        <span className="text-sm font-medium text-gray-700 mb-1.5 flex items-center gap-2">
          <Lock className="w-4 h-4" /> 4-digit PIN
        </span>
        <input
          type="password"
          required
          inputMode="numeric"
          pattern="\d{4}"
          maxLength={4}
          placeholder="••••"
          value={pin}
          onChange={(e) => { setPin(e.target.value.replace(/\D/g, '').slice(0, 4)); setError(null); }}
          data-testid="pin-login-pin-input"
          className="w-full px-4 py-3 text-center text-2xl tracking-[0.6em] font-mono rounded-xl border border-gray-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none transition"
        />
      </label>
      {error && <p className="text-sm text-red-600" data-testid="pin-login-error">{error}</p>}
      <button
        type="submit"
        disabled={loading || !email || pin.length !== 4}
        data-testid="pin-login-submit-button"
        className={`w-full ${accentClass} text-white py-3 rounded-xl font-medium transition flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed`}
      >
        {loading ? (<><Loader2 className="w-4 h-4 animate-spin" /> Signing in…</>) : 'Sign in'}
      </button>
    </form>
  );
}
